import React from "react";
import { Input } from "antd";

// Readable text for a failed API call — accepts the response body or the raw
// axios error, and always returns a string (safe to pass to message.error).
export function getApiError(error, fallback = "Something went wrong") {
  const body = error?.response?.data ?? error;
  // A code bug (TypeError…) caught here shouldn't surface as a toast
  const isCodeError = body instanceof Error && !body.isAxiosError;
  const text =
    body?.apiStatus?.statusMessage ||
    body?.statusMessage ||
    body?.data?.message ||
    (isCodeError ? "" : body?.message) ||
    (typeof body === "string" ? body : "");
  return typeof text === "string" && text.trim() && !text.trim().startsWith("<")
    ? text
    : fallback;
}

// Multipart body for a save: skips null/undefined, appends File/Blob as-is
// and everything else as text.
export function toFormData(fields) {
  const formData = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    formData.append(key, value instanceof Blob ? value : String(value));
  });
  return formData;
}

// Indian identity / bank formats, as antd Form rules: rules={[FORMAT_RULES.pan]}
export const FORMAT_RULES = {
  mobile: {
    pattern: /^[6-9]\d{9}$/,
    message: "Enter a valid 10-digit mobile number",
  },
  // Any 10-digit number not starting with 0 (seller portal rule — sellers
  // may give a landline); `mobile` above is for personal mobiles
  phone: {
    pattern: /^[1-9]\d{9}$/,
    message: "Enter a valid 10-digit phone number",
  },
  pinCode: { pattern: /^\d{6}$/, message: "Pincode must be 6 digits" },
  aadhaar: { pattern: /^\d{12}$/, message: "Aadhaar number must be 12 digits" },
  pan: {
    pattern: /^[A-Z]{5}[0-9]{4}[A-Z]$/,
    message: "Enter a valid PAN (e.g. ABCDE1234F)",
  },
  ifsc: {
    pattern: /^[A-Z]{4}0[A-Z0-9]{6}$/,
    message: "Enter a valid 11-character IFSC (e.g. SBIN0001234)",
  },
  accountNumber: {
    pattern: /^\d{9,18}$/,
    message: "Account number must be 9–18 digits",
  },
  gstin: {
    pattern: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/,
    message: "Enter a valid 15-character GSTIN (e.g. 27ABCDE1234F1Z5)",
  },
  cin: {
    pattern: /^[LU][0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/,
    message: "Enter a valid 21-character CIN",
  },
};

const IMAGE_TYPE_LABELS = {
  "image/png": "PNG",
  "image/jpeg": "JPG",
  "image/webp": "WEBP",
  "application/pdf": "PDF",
};

// Short requirement text for an image field, e.g.
// "PNG, JPG or WEBP • Square, min 200×200 • Max 2 MB"
export function imageRulesHint(rules) {
  const names = rules.types.map((t) => IMAGE_TYPE_LABELS[t] || t);
  const types =
    names.length > 1
      ? `${names.slice(0, -1).join(", ")} or ${names[names.length - 1]}`
      : names[0];
  const size = rules.minWidth
    ? `${rules.square ? "Square, min" : "Min"} ${rules.minWidth}×${rules.minHeight}`
    : rules.square
      ? "Square"
      : "";
  return [types, size, `Max ${rules.maxSizeMB} MB`].filter(Boolean).join(" • ");
}

function readImageSize(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      reject(new Error("unreadable"));
      URL.revokeObjectURL(url);
    };
    img.src = url;
  });
}

// Checks an image file against rules (constants/ImageUpload); resolves to an
// error message, or null when the file is valid.
export async function validateImageFile(file, rules) {
  const names = rules.types.map((t) => IMAGE_TYPE_LABELS[t] || t).join(", ");
  if (!rules.types.includes(file.type)) {
    const noun = rules.types.includes("application/pdf") ? "files" : "images";
    return `Only ${names} ${noun} are allowed`;
  }
  if (file.size > rules.maxSizeMB * 1024 * 1024) {
    return `Image must be ${rules.maxSizeMB} MB or smaller`;
  }
  // A PDF (allowed for documents) has no pixel size to check
  if (file.type === "application/pdf") return null;
  let size;
  try {
    size = await readImageSize(file);
  } catch {
    return "This file isn't a valid image";
  }
  const { width, height } = size;
  if (
    (rules.minWidth && width < rules.minWidth) ||
    (rules.minHeight && height < rules.minHeight)
  ) {
    return `Image must be at least ${rules.minWidth}×${rules.minHeight} (this one is ${width}×${height})`;
  }
  if (
    rules.square &&
    Math.abs(width - height) / Math.max(width, height) >
      (rules.squareTolerance ?? 0)
  ) {
    return `Image must be square (this one is ${width}×${height})`;
  }
  return null;
}

export function throttle(mainFunction, delay) {
  let timerFlag = null; // Variable to keep track of the timer

  // Returning a throttled version
  return (...args) => {
    if (timerFlag === null) {
      // If there is no timer currently running
      timerFlag = setTimeout(() => {
        mainFunction(...args); // Execute the main function
        // Set a timer to clear the timerFlag after the specified delay
        timerFlag = null; // Clear the timerFlag to allow the main function to be executed again
      }, delay);
    }
  };
}

export function debounce(mainFunction, delay) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    setTimeout(() => {
      mainFunction(...args);
    }, delay);
  };
}

export function isNumeric(str) {
  return /^\d+$/.test(str) || /^\d+\.\d+$/.test(str);
}

export function lookupList(list, lookupKey, lookupValue) {
  if (!list || !Array.isArray(list)) {
    return null;
  }
  return list.reduce((acc, curr) => {
    const key = curr[lookupKey];
    if (!acc[key]) {
      acc[key] = curr[lookupValue];
    }
    return acc;
  }, {});
}

export function isNumbesOnly(value) {
  return /^[0-9]+$/.test(value);
}

export const RenderText = ({ text, textAlign, padding = "40px", styles }) => {
  return (
    <div
      style={{
        textAlign: textAlign || "center",
        paddingRight: textAlign === "RIGHT" ? padding : "0",
        ...styles,
      }}
    >
      {text}
    </div>
  );
};

export function TryParseFloat(str, defaultValue) {
  var retValue = defaultValue;
  if (str !== null) {
    if (str.length > 0) {
      if (!isNaN(str)) {
        retValue = parseFloat(str);
      }
    }
  }
  return retValue;
}

export const orderStatusColor = (orderStatus) => {
  const LIST = [
    "REV_LOT_INSPECTION_AT_FC",
    "DAMAGED_BY_AFFILIATE",
    "DECLINED_BY_CUSTOMER_AT_DELIVERY",
    "DECLINED_BY_SHIPPER",
    "ADMIN_REJECTED",
    "REV_LOT_CREATE_BY_BLOCK_TO_FC",
    "REV_SHIPPER_ACCEPTANCE",
    "REV_LOT_INSPECTION_AT_SHIPPER",
    "REV_PENDING_FC_ACCEPTANCE",
    "REV_PENDING_SHIPPER_ACCEPTANCE",
    "ORDER_CANCELLED_BEFORE_BOOKING",
    "REV_ACCEPTED_BY_SHIPPER",
    "REV_LOT_CREATE_BY_FC",
    "LOT_DAMAGED",
    "CANCELLED",
    "RETURNED",
    "REJECTED",
    "REV_LOT_CREATE_BY_AZP",
    "DISPUTED",
  ];
  return LIST.includes(orderStatus)
    ? "red"
    : orderStatus === "DELIVERED_TO_CUSTOMER"
      ? "green"
      : null;
};

export const renderText = (text, textAlign, padding = "40px") => {
  // Convert "RIGHT" to "right" for CSS compatibility
  const cssTextAlign =
    textAlign === "RIGHT"
      ? "right"
      : textAlign === "LEFT"
        ? "left"
        : textAlign === "CENTER"
          ? "center"
          : textAlign || "center";

  return (
    <div
      style={{
        textAlign: cssTextAlign,
        paddingRight: textAlign === "RIGHT" ? padding : "0",
        width: "100%",
        display: "block",
      }}
    >
      {text}
    </div>
  );
};

export const thousandSeparator = (value) => {
  if (!value) return "";
  return String(value).replace(/(?<!\..*)(\d)(?=(?:\d{3})+(?:\.|$))/g, "$1,");
};

export function TryParseInt(value, defaultValue = 0) {
  if (value === undefined || value === null) return defaultValue;

  const cleaned = value.toString().replace(/,/g, ""); // remove commas
  const parsed = parseInt(cleaned, 10);

  return isNaN(parsed) ? defaultValue : parsed;
}

export const lakhSeparator = (value, decimalPlaces = 2) => {
  if (!value) return 0;
  const newValue = TryParseInt(value.toString(), 0);
  if (newValue > 0 && newValue < 100000) {
    return String(newValue).replace(
      /(?<!\..*)(\d)(?=(?:\d{3})+(?:\.|$))/g,
      "$1,",
    );
  } else {
    return newValue.toLocaleString("en-IN", {
      maximumFractionDigits: decimalPlaces,
      // style: 'currency',
      // currency: 'INR'
    });
  }
};

// Formats today's date as YYYY-MM-DD
export function getCurrentDate() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export const getCurrentDateTime = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");
  // Return formatted date and time like "2024-11-07_15-30-45"
  return `${year}-${month}-${day}_${hours}h:${minutes}m:${seconds}s`;
};

export const getCurrentDateTimeMinutes = () => {
  const now = new Date();
  const pad = (n) => (n < 10 ? "0" + n : n);

  const year = now.getFullYear();
  const month = pad(now.getMonth() + 1);
  const day = pad(now.getDate());
  const hours = pad(now.getHours());
  const minutes = pad(now.getMinutes());

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};
export const dateFormatter = (input, withoutTime) => {
  if (!input) return "Invalid Date";

  let date;

  // Handle "DD-MM-YYYY HH:mm:ss"
  if (/^\d{2}-\d{2}-\d{4} \d{2}:\d{2}:\d{2}$/.test(input)) {
    const [datePart, timePart] = input.split(" ");
    const [day, month, year] = datePart.split("-");
    const [hours, minutes, seconds] = timePart.split(":");
    date = new Date(`${year}-${month}-${day}T${hours}:${minutes}:${seconds}`);
  } else {
    // Handle other formats (like ISO or SQL-style)
    let dateString = input;

    if (dateString.includes(" ") && !dateString.includes("T")) {
      dateString = dateString.replace(" ", "T");
    }

    if (dateString.includes(".")) {
      dateString = dateString.split(".")[0];
    }

    if (dateString.endsWith("Z")) {
      dateString = dateString.replace("Z", "");
    }

    date = new Date(dateString);
  }

  if (isNaN(date)) return "Invalid Date";

  const year = date.getFullYear();
  const month = `0${date.getMonth() + 1}`.slice(-2);
  const day = `0${date.getDate()}`.slice(-2);

  if (!withoutTime) {
    let hours = date.getHours();
    const minutes = `0${date.getMinutes()}`.slice(-2);
    const ampm = hours >= 12 ? "PM" : "AM";

    hours = hours % 12 || 12;
    const formattedHours = `0${hours}`.slice(-2);

    return `${year}-${month}-${day} ${formattedHours}:${minutes} ${ampm}`;
  } else {
    return `${year}-${month}-${day}`;
  }
};

// to generate financial year list
export const getFinancialYears = () => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth(); // 0 = Jan

  // Financial year starts in April
  const fyStartYear = currentMonth < 3 ? currentYear - 1 : currentYear;

  return Array.from({ length: 5 }).map((_, i) => {
    const startYear = fyStartYear - i;
    const endYear = startYear + 1;

    return {
      value: i,
      _value: `${startYear}-04-01~${endYear}-03-31`,
      label: `FY ${startYear}-${endYear.toString().slice(2)}`,
    };
  });
};

function base64ToArrayBuffer(data) {
  var bString = window.atob(data);
  var bLength = bString.length;
  var bytes = new Uint8Array(bLength);
  for (var i = 0; i < bLength; i++) {
    var ascii = bString.charCodeAt(i);
    bytes[i] = ascii;
  }
  return bytes;
}

export function base64toPDF(data, invoice_number) {
  var bufferArray = base64ToArrayBuffer(data);
  var blobStore = new Blob([bufferArray], { type: "application/pdf" });
  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(blobStore);
    return;
  }
  var data = window.URL.createObjectURL(blobStore);
  var link = document.createElement("a");
  document.body.appendChild(link);
  link.href = data;
  link.download = invoice_number + ".pdf";
  link.click();
  window.URL.revokeObjectURL(data);
  link.remove();
}

function parseDate(dateStr) {
  if (!dateStr) return 0;
  const [datePart, timePart] = dateStr.split(" ");
  const [year, month, day] = datePart.split("-");
  const [hour, minute, secondWithMs] = timePart.split(":");
  const [second, millisecond] = secondWithMs.split(".");

  // Construct a valid Date object using the parsed components
  const formattedDate = `${year}-${month}-${day}T${hour}:${minute}:${second}.${millisecond}`;
  return new Date(formattedDate).getTime();
}

// Reusable sorter function that can be applied to different columns format 2024-11-11 16:09:19.439
export function dateSorter(a, b, dateField) {
  try {
    const dateA = parseDate(a[dateField]);
    const dateB = parseDate(b[dateField]);

    if (isNaN(dateA) || isNaN(dateB)) return 0;
    return dateB - dateA; // Sorting in descending order (if you need ascending, switch the return)
  } catch (error) {
    console.error("Sorting error:", error);
    return 0;
  }
}
export const isAgrozoneEmailValid = (emails) => {
  const list = emails.split(",");
  let isValid = true;
  if (list.length) {
    list.forEach((email) => {
      if (
        email.indexOf("agrozone.in") !== -1 &&
        !String(email)
          .toLowerCase()
          .match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
          )
      ) {
        isValid = false;
      }
    });
  }
  return isValid;
};
export const allowOnlyNumbers = (e) => {
  const charCode = e.which ? e.which : e.keyCode;
  const charStr = String.fromCharCode(charCode);

  // Block spaces (especially leading spaces)
  if (charStr === " ") {
    e.preventDefault();
    return;
  }

  // Block non-numeric and non-dot characters
  if (!/[0-9.]/.test(charStr)) {
    e.preventDefault();
    return;
  }

  // Block more than one dot
  if (charStr === "." && e.target.value.includes(".")) {
    e.preventDefault();
  }
};

// utils/formatters.js
export const formatPAN = (text) => {
  let input = text.toUpperCase();
  let formatted = "";

  for (let i = 0; i < input.length && i < 10; i++) {
    const char = input[i];
    if (i < 5) {
      if (/[A-Z]/.test(char)) formatted += char;
      else break;
    } else if (i < 9) {
      if (/[0-9]/.test(char)) formatted += char;
      else break;
    } else if (i === 9) {
      if (/[A-Z]/.test(char)) formatted += char;
      else break;
    }
  }
  return formatted;
};

export const formatGST = (text) => {
  let input = text.toUpperCase();
  let formatted = "";

  for (let i = 0; i < input.length && i < 15; i++) {
    const char = input[i];
    if (i < 2) {
      if (/[0-9]/.test(char)) formatted += char;
      else break;
    } else if (i < 7) {
      if (/[A-Z]/.test(char)) formatted += char;
      else break;
    } else if (i < 11) {
      if (/[0-9]/.test(char)) formatted += char;
      else break;
    } else if (i === 11) {
      if (/[A-Z]/.test(char)) formatted += char;
      else break;
    } else if (i === 12) {
      if (/[A-Z0-9]/.test(char)) formatted += char;
      else break;
    } else if (i === 13) {
      if (char === "Z") formatted += char;
      else break;
    } else if (i === 14) {
      if (/[A-Z0-9]/.test(char)) formatted += char;
      else break;
    }
  }
  return formatted;
};

export const normalizeAadhaar = (text = "") => {
  let digits = text.replace(/\D/g, "").slice(0, 12);
  let formatted = "";
  for (let i = 0; i < digits.length; i++) {
    formatted += digits[i];
    if ((i + 1) % 4 === 0 && i !== digits.length - 1) {
      formatted += " ";
    }
  }
  return formatted;
};

export const normalizeDrivingLicense = (text = "") => {
  let input = text
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 15);
  let formatted = "";

  for (let i = 0; i < input.length; i++) {
    const char = input[i];

    if (i < 2) {
      // First two characters must be letters
      if (/[A-Z]/.test(char)) {
        formatted += char;
      } else {
        break;
      }
    } else if (i < 4) {
      // Next two characters must be digits
      if (/[0-9]/.test(char)) {
        formatted += char;
      } else {
        break;
      }
    } else {
      // Rest can be alphanumeric
      if (/[A-Z0-9]/.test(char)) {
        formatted += char;
      } else {
        break;
      }
    }
  }

  // Insert space after 4 characters (if length > 4)
  if (formatted.length > 4) {
    formatted = formatted.slice(0, 4) + " " + formatted.slice(4);
  }

  return formatted;
};

// export const getStateCode = (state) => {
//   console.log("IN STATE CODE METHOD: ", state);
//   const State_List = {
//     maharashtra: "MH",
//     gujrat: "GJ",
//     gujarat: "GJ",
//     rajasthan: "RJ",
//     odisha: "OD",
//   };

//   return !!state ? State_List[state.toLowerCase()] : state;
// };

const formatDate = (date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export const getStateCode = (state) => {
  console.log("IN STATE CODE METHOD: ", state);
  const State_List = {
    maharashtra: "MH",
    gujrat: "GJ",
    gujarat: "GJ",
    rajasthan: "RJ",
    odisha: "OD",
  };

  return !!state ? State_List[state.toLowerCase()] : state;
};

export const getLastMonthRange = () => {
  const now = new Date();
  const firstDayLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastDayLastMonth = new Date(now.getFullYear(), now.getMonth(), 0); // day 0 gives last day of prev month

  return {
    from: formatDate(firstDayLastMonth),
    to: formatDate(lastDayLastMonth),
  };
};
export const getLastThreeMonthsRange = () => {
  const now = new Date();

  // copy of current date
  const startDate = new Date(now);
  startDate.setMonth(startDate.getMonth() - 3); // go 3 months back

  return {
    from: formatDate(startDate),
    to: formatDate(now),
  };
};

export const capitalizeFirst = (value = "") => {
  if (!value) return "";

  value = value.trimStart();

  return value
    .toLowerCase()
    .split(/(\s|')/) // split by spaces/apostrophes, keep delimiters
    .map((word) => {
      if (!word.trim()) return word; // preserve spaces/apostrophes
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join("");
};

export const capitalizeWords = (value) => {
  if (!value) return "";
  return value
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const smallWords = ["of", "and", "or", "in", "on", "for"];

/**
 * Readable label from a data key: "userName" / "user_name" / "user-name"
 * → "User Name" (splits camelCase / snake / kebab, then toTitleCase).
 * Used where a key would otherwise show as raw text (e.g. table labels).
 */
export function humanizeKey(key = "") {
  return toTitleCase(
    String(key)
      .replace(/[_-]+/g, " ")
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .trim(),
  );
}

export function toTitleCase(str = "") {
  return str
    .trimStart()
    .toLowerCase()
    .split(/(\s|')/)
    .map((word) => {
      if (!word) return word;
      if (smallWords.includes(word.toLowerCase())) return word.toLowerCase();
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join("");
}

export const getFormattedDateTime = (date = new Date()) => {
  const pad = (n) => n.toString().padStart(2, "0");

  let hours = date.getHours();
  const minutes = pad(date.getMinutes());
  const ampm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12 || 12;
  hours = pad(hours);

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());

  return `${year}-${month}-${day}_${hours}-${minutes}_${ampm}`;
};
